import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import { useAuthorization } from 'framework/authorization';
import { ShowSection } from 'framework/ui/layout';
import { useEmployeeId } from 'domain/shared/employee';
import { InputLoader } from 'framework/ui/mui-builder';
import { useListContext } from 'framework/list';
import EducationCreateForm from '../createupdate/EducationCreateForm';
import EducationItem from '../shared/EducationItem';
import { READ_EDUCATIONS_LIST } from 'domain/shared/employee';
import { CREATE_EDUCATION, Education } from '../shared/types';
import PrepopulateButton from 'domain/people/employee/prepopulation/PrepopulateButton';
import { usePrepopulationContext } from 'domain/people/employee/prepopulation/PrepopulationContext';
import EducationPrepopulateModal from '../createupdate/EducationPrepopulateModal';

const EducationListContent = () => {
  const { data, loading, reloadData } = useListContext();
  const { isGranted } = useAuthorization();
  const [isCreateUpdate, setIsCreateUpdate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isPrepopulateModalOpen, setIsPrepopulateModalOpen] = useState(false);

  const employeeId = useEmployeeId();
  const { forms = [] } = usePrepopulationContext();
  const items = data?.items || [];

  const hasAccess = isGranted(READ_EDUCATIONS_LIST);
  const canCreateEducation = isGranted(CREATE_EDUCATION);
  const hasItems = items.length > 0;
  const hasPrepopulationForms = forms.length > 0 && !hasItems;

  if (!hasAccess) {
    return null;
  }

  const handleUpdate = () => {
    setIsCreateUpdate(false);
    reloadData?.();
  };

  const handleCreateSave = () => {
    setIsCreating(false);
    setIsCreateUpdate(false);
    reloadData?.();
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
    setIsCreateUpdate(false);
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setIsCreateUpdate(true);
  };

  const handlePrepopulateModalSave = () => {
    setIsPrepopulateModalOpen(false);
    reloadData?.();
  };

  const handlePrepopulateModalClose = () => {
    setIsPrepopulateModalOpen(false);
  };

  const getEducationKey = (education: Education) => {
    return `${education.id}-${education.document?.scansIds?.length || 0}-${JSON.stringify(education.document?.scansIds)}`;
  };

  const renderAddButton = () => {
    if (!canCreateEducation || isCreateUpdate) {
      return null;
    }

    return (
      <Button onClick={handleStartCreate} startIcon={<Add />} variant="outlined" size="small" sx={{ marginTop: 1.5 }}>
        Add education
      </Button>
    );
  };

  const renderPrepopulateButton = () => {
    if (!hasPrepopulationForms) {
      return null;
    }

    return <PrepopulateButton scrollToPrepopulatedInputs={() => setIsPrepopulateModalOpen(true)} />;
  };

  const renderLoadingState = () => (
    <Box display="flex" justifyContent="center">
      <InputLoader />
    </Box>
  );

  const renderEmptyState = () => (
    <Box display="flex" flexDirection="column" alignItems="center" p={1}>
      <Typography variant="body1" color="textSecondary" mb={3} textAlign="center">
        No education records have been added for this worker yet.
      </Typography>
      <Box display="flex" gap={1} alignItems="center">
        {renderAddButton()}
        {renderPrepopulateButton()}
      </Box>
    </Box>
  );

  const renderCreateForm = () => (
    <EducationCreateForm employeeId={employeeId!} onSave={handleCreateSave} onCancel={handleCreateCancel} />
  );

  const renderItemsList = () => (
    <>
      {items.map((education: Education) => (
        <EducationItem
          key={getEducationKey(education)}
          education={education}
          onUpdate={handleUpdate}
          onStartEdit={() => setIsCreateUpdate(true)}
          onCancelEdit={() => setIsCreateUpdate(false)}
          disabled={isCreateUpdate}
        />
      ))}

      {isCreating && employeeId && renderCreateForm()}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
        {renderAddButton()}
        {renderPrepopulateButton()}
      </Box>
    </>
  );

  const renderPrepopulateModal = () => (
    <EducationPrepopulateModal
      isOpen={isPrepopulateModalOpen}
      onClose={handlePrepopulateModalClose}
      onSave={handlePrepopulateModalSave}
    />
  );

  const showSectionProps = {
    sx: {
      position: 'relative',
      '.show-title-container': {
        marginBottom: '10px',
      },
    } as const,
    title: (
      <SchoolIcon
        sx={{
          color: 'text.primary',
        }}
      />
    ),
    disableDefaultPadding: true,
  };

  const getContent = () => {
    if (loading) {
      return renderLoadingState();
    }

    if (!hasItems && !isCreating) {
      return (
        <>
          {renderEmptyState()}
          {renderPrepopulateModal()}
        </>
      );
    }

    if (!hasItems && isCreating) {
      return renderCreateForm();
    }

    return (
      <>
        {renderItemsList()}
        {renderPrepopulateModal()}
      </>
    );
  };

  return (
    <ShowSection {...showSectionProps}>
      <Box>{getContent()}</Box>
    </ShowSection>
  );
};

export default EducationListContent;
